import { API_URL } from '../config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const CreatorsLanding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from URL parameter
  const searchParams = new URLSearchParams(location.search);
  const emailFromUrl = searchParams.get('email') || '';

  const [formData, setFormData] = useState({
    email: emailFromUrl,
    platform: '',
    handle: '',
    price: '',
    videoDeletePolicy: false,
    paypalPaymentAgreement: false,
    audienceCountryFile: null as File | null
  });

  // File upload states
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [extractedCountries, setExtractedCountries] = useState<Record<string, number> | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailFromUrl, setIsEmailFromUrl] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'error' | 'success' | 'info' | 'warning'>('error');
  const [isDragOver, setIsDragOver] = useState(false);

  // 로딩 페이지에서 돌아온 에러 처리
  useEffect(() => {
    const errorMessage = location.state?.error;
    if (errorMessage) {
      showToastMessage(errorMessage, 'error');
      // Clear the error state
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state?.error]);

  // Update email when URL parameter changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({
        ...prev,
        email: emailParam
      }));
      setIsEmailFromUrl(true); // Mark that email came from URL
    }
  }, [location.search]);

  // Auto-hide toast after 5 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const showToastMessage = (message: string, type: 'error' | 'success' | 'info' | 'warning' = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'price') {
      // Only allow numbers and one decimal point for price field
      const numericValue = (value || '').replace(/[^0-9.]/g, '');
      // Prevent multiple decimal points
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else if (name === 'handle') {
      // Handle formatting: lowercase, no spaces, Instagram/TikTok allowed characters only
      let cleanValue = (value || '').toLowerCase().replace(/\s/g, ''); // Remove spaces and convert to lowercase

      // Remove @ at the beginning if user typed it
      if (cleanValue.startsWith('@')) {
        cleanValue = cleanValue.substring(1);
      }

      // Only allow alphanumeric, periods, and underscores (Instagram/TikTok rules)
      cleanValue = cleanValue.replace(/[^a-z0-9._]/g, '');

      setFormData(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploadStatus('uploading');
    setUploadError(null);
    setExtractedCountries(null);

    const formDataToSend = new FormData();
    formDataToSend.append('audienceCountryFile', file);

    try {
      const response = await fetch(`${API_URL}/api/responses/upload-audience`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to upload file');
      }

      setUploadedFileUrl(result.data.url);
      if (result.data && result.data.extractedCountries) {
        setExtractedCountries(result.data.extractedCountries);
      }
      setUploadStatus('success');
      showToastMessage('File uploaded successfully', 'success');
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setUploadError(error.message || 'Failed to upload file');
      showToastMessage(error.message || 'Failed to upload file', 'error');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showToastMessage('File size exceeds 10MB limit', 'error');
        return;
      }

      setFormData(prev => ({
        ...prev,
        audienceCountryFile: file
      }));
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showToastMessage('File size exceeds 10MB limit', 'error');
        return;
      }

      setFormData(prev => ({
        ...prev,
        audienceCountryFile: file
      }));
      handleFileUpload(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all required fields
    if (!formData.email || !formData.platform || !formData.handle || !formData.price) {
      showToastMessage('Please fill in all required fields', 'error');
      return;
    }

    // Validate audience file upload
    if (!uploadedFileUrl || uploadStatus !== 'success') {
      showToastMessage('Please upload audience country file', 'error');
      return;
    }

    // Validate agreements
    if (!formData.videoDeletePolicy || !formData.paypalPaymentAgreement) {
      showToastMessage('Please agree to all terms', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to API using the same endpoint as CollaborationInquiry
      const response = await fetch(`${API_URL}/api/responses/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          platform: formData.platform,
          handle: formData.handle,
          price: parseFloat(formData.price),
          videoDeletePolicy: formData.videoDeletePolicy,
          paypalPaymentAgreement: formData.paypalPaymentAgreement,
          audienceFileUrl: uploadedFileUrl,
          extractedCountries: extractedCountries,
          inquiryType: 'dstribute' // Mark this as a Dstribute creator inquiry
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      if (result.success) {
        // Redirect directly to thank you page
        navigate('/thank-you', {
          state: {
            fromInquiry: true,
            inquiryId: result.data._id
          }
        });
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      showToastMessage(error.message || 'Failed to submit form. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '120px',
      paddingBottom: '2rem',
      paddingLeft: '2rem',
      paddingRight: '2rem',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      background: '#ffffff',
    }}>
      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: toastType === 'error' ? '#f44336' :
                         toastType === 'success' ? '#4caf50' :
                         toastType === 'info' ? '#2196f3' : '#ff9800',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          animation: 'slideIn 0.3s ease-out',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>{toastMessage}</span>
          <button
            onClick={() => setShowToast(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '20px',
              padding: 0,
              marginLeft: '10px'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{
        width: '100%',
        maxWidth: '600px',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        padding: '2.5rem',
        border: '1px solid #f3f4f6'
      }}>
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '1rem'
          }}>
            For Creators 
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#666',
            marginBottom: '1rem'
          }}>
            
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#555',
            lineHeight: '1.6'
          }}>
          Get paid to create content for trending software companies. Join our network of creators and start earning!
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Email <span style={{ color: '#e74c3c' }}>*</span>
              {isEmailFromUrl && (
                <span style={{
                  color: '#28a745',
                  fontSize: '0.9rem',
                  fontWeight: '400',
                  marginLeft: '0.5rem'
                }}>
                  (Pre-filled from invitation)
                </span>
              )}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              readOnly={isEmailFromUrl}
              placeholder="your@email.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: isEmailFromUrl ? '#f5f5f5' : 'white',
                cursor: isEmailFromUrl ? 'not-allowed' : 'text',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => {
                if (!isEmailFromUrl) {
                  e.target.style.borderColor = '#ff4000';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e1e8ed';
              }}
            />
          </div>

          {/* Platform */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Platform <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <select
              name="platform"
              value={formData.platform}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff4000'}
              onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
            >
              <option value="">Select platform</option>
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
            </select>
          </div>

          {/* Handle */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Username / Handle <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <input
              type="text"
              name="handle"
              value={formData.handle}
              onChange={handleInputChange}
              required
              placeholder="@yourusername"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff4000'}
              onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
            />
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
              Your {formData.platform || 'social media'} handle without @
            </p>
          </div>

          {/* Price */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Price per Video <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <input
              type="text"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              required
              placeholder="500"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff4000'}
              onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
            />
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              marginTop: '0.25rem'
            }}>
              Rate in <strong>USD ($)</strong> per 1 minute video post (PayPal Fee incl.)
            </p>
          </div>

          {/* Audience Country */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Audience Country <span style={{ color: '#e74c3c' }}>*</span>
            </label>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              marginBottom: '0.5rem'
            }}>
              Screenshot of audience country demographics. Please attach the image (JPG, PNG, GIF, WebP) or PDF file directly. <strong>Max 10MB</strong>.
            </p>

            {/* Guide Image */}
            <div style={{
              marginBottom: '1rem',
              padding: '1rem',
              background: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.5rem' }}>
                📋 <strong>Example:</strong> How to find audience demographics
              </p>
              <img
                src="https://storage.tally.so/ac05fa4e-95ab-4b33-bd00-202acfaada86/unnamed.png"
                alt="Audience demographics example"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            {/* Drag and Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('audienceCountryFile')?.click()}
              style={{
                width: '100%',
                minHeight: '120px',
                border: `2px dashed ${isDragOver ? '#ff4000' : (formData.audienceCountryFile ? '#28a745' : '#e1e8ed')}`,
                borderRadius: '8px',
                background: isDragOver ? '#fff5f0' : (formData.audienceCountryFile ? '#f8fff9' : '#fafbfc'),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                padding: '1rem',
                position: 'relative'
              }}
            >
              {uploadStatus === 'uploading' ? (
                <div style={{
                  textAlign: 'center',
                  color: '#ff4000'
                }}>
                  <div style={{
                    fontSize: '2rem',
                    marginBottom: '0.5rem',
                    animation: 'spin 2s linear infinite'
                  }}>
                    ⏳
                  </div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    Uploading file...
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    Please wait while we upload your file
                  </div>
                </div>
              ) : uploadStatus === 'success' ? (
                <div style={{
                  textAlign: 'center',
                  color: '#28a745'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    File uploaded successfully!
                  </div>
                  {formData.audienceCountryFile && (
                    <>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        {formData.audienceCountryFile.name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {(formData.audienceCountryFile.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </>
                  )}
                  {extractedCountries && Object.keys(extractedCountries).length > 0 && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: '#28a745',
                      marginTop: '0.5rem'
                    }}>
                      ✓ Audience data extracted
                    </div>
                  )}
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#999',
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    Click to upload a different file
                  </div>
                </div>
              ) : uploadStatus === 'error' ? (
                <div style={{
                  textAlign: 'center',
                  color: '#dc3545'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>❌</div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    Upload failed
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#dc3545' }}>
                    {uploadError || 'Failed to upload file'}
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#666',
                    marginTop: '0.5rem'
                  }}>
                    Click to try again
                  </div>
                </div>
              ) : formData.audienceCountryFile ? (
                <div style={{
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    {formData.audienceCountryFile.name}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {(formData.audienceCountryFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#999',
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    Click to upload a different file
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📎</div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    Click to upload or drag and drop
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#999' }}>
                    JPG, PNG, GIF, WebP, PDF (max 10MB)
                  </div>
                </div>
              )}
            </div>

            <input
              type="file"
              id="audienceCountryFile"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* Notice */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            background: '#fef9f3',
            borderRadius: '8px',
            border: '1px solid #ffd8a8'
          }}>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              lineHeight: '1.5'
            }}>
              ⚠️ <strong>Note:</strong> This form is for a single account submission.
              If you'd like to submit for both TikTok and Instagram, please provide each account separately with its own account name, price, and audience response. You can submit additional entries at{' '}
              <a
                href="https://dstribute.ai/creator"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#ff4000',
                  textDecoration: 'underline'
                }}
              >
                https://dstribute.ai/creator
              </a>
            </p>
          </div>

          {/* Agreements */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              cursor: 'pointer',
              marginBottom: '1rem'
            }}>
              <input
                type="checkbox"
                name="videoDeletePolicy"
                checked={formData.videoDeletePolicy}
                onChange={handleInputChange}
                required
                style={{
                  marginRight: '0.75rem',
                  marginTop: '0.25rem',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.5' }}>
                I agree to NOT DELETE videos within 60 days of posting <span style={{ color: '#e74c3c' }}>*</span>
              </span>
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                name="paypalPaymentAgreement"
                checked={formData.paypalPaymentAgreement}
                onChange={handleInputChange}
                required
                style={{
                  marginRight: '0.75rem',
                  marginTop: '0.25rem',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.5' }}>
                I agree to receive PayPal Payment <span style={{ color: '#e74c3c' }}>*</span>
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '1rem',
              background: isSubmitting ? '#ccc' : '#ff4000',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.background = '#ff6633';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.background = '#ff4000';
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreatorsLanding;